import debounce from 'lodash.debounce';
import React, {
  Context,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getAnalytics, getSalesOrders } from '../components/API';
import { is8DataApp } from '../utils/env';

interface Error {
  message: string;
  type: string;
}
export interface Order {
  date: string;
  quantity: number;
}

interface State {
  analytics: any;
  orders: Order[];
  initialize: (...args) => any;
  isLoading: boolean;
  errors: Error[];
}

export const DashboardAnalyticsContext: Context<State> = createContext({
  analytics: {},
  orders: [],
  initialize: () => {},
  isLoading: false,
  errors: undefined,
});

export function DashboardAnalyticsProvider(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState();
  const [isInitialized, setIsInitialized] = useState(false);
  const [analytics, setAnalytics] = useState({});
  const [orders, setOrders] = useState([]);
  function initialize() {
    if (!is8DataApp()) return;
    if (isInitialized || isLoading) return;
    setIsLoading(true);
    Promise.allSettled([getSalesOrders(), getAnalytics()])
      .then((settled) => {
        const rejected = settled.filter(
          (result) => result.status === 'rejected',
        );

        if (rejected.length > 0) {
          const errors = rejected.map(
            (result) => (result as PromiseRejectedResult).reason,
          );
          errors.forEach((error) => appendError(error));
        }

        const [ordersResult, analyticsResult] = settled;
        ordersResult['value'] &&
          handleResult(
            (ordersResult as PromiseFulfilledResult<any>).value,
            setOrders,
          );
        analyticsResult['value'] &&
          handleResult(
            (analyticsResult as PromiseFulfilledResult<any>).value,
            setAnalytics,
          );
        setIsInitialized(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }
  function handleResult(result: any, setResult: (value) => void) {
    if (result?.error) {
      appendError(result.error);
    } else {
      setResult(result);
    }
  }
  function appendError(error: any) {
    console.error('ERROR', error);
    const newErrors = errors ?? [];
    newErrors.push(error);
    setErrors(newErrors as any);
  }
  const debouncedInitialize = debounce(initialize, 1000, {
    leading: true,
    trailing: false,
  });
  useEffect(initialize, []);
  return (
    <DashboardAnalyticsContext.Provider
      value={{
        analytics,
        initialize: debouncedInitialize,
        isLoading,
        errors,
        orders,
      }}
    >
      {props.children}
    </DashboardAnalyticsContext.Provider>
  );
}

export const useDashboardAnalytics = () => {
  const { initialize, ...restProps } = useContext(DashboardAnalyticsContext);
  useEffect(() => {
    initialize();
  }, [initialize]);
  return restProps;
};
